import React from 'react';
import FeaturePage from './FeaturePage';
import { Box, Container, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import performanceMetricsScreenshot from '../assets/performance-metrics-screenshot.png'; // Import the screenshot
import { motion } from 'framer-motion';

// Animation Variants (same as InventoryManagementPage)
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: "easeOut" } }
};

function PerformanceMetricsPage() {

  // Features derived from the screenshot
  const metricsFeatures = [
    { icon: <DashboardIcon color="primary"/>, text: "Centralized Dashboard: Key Performance Indicators (KPIs) at a glance." },
    { icon: <DirectionsCarIcon color="primary"/>, text: "Inventory Overview: Track active inventory count, value, floor plan vs. dealer-owned units." },
    { icon: <AttachMoneyIcon color="primary"/>, text: "Financial Snapshot: Monitor monthly profit, admin costs, deposits, and total reconditioning investment." },
    { icon: <BuildIcon color="primary"/>, text: "Reconditioning Insights: View monthly reconditioning spend and total cost for unsold inventory." },
    { icon: <ScheduleIcon color="primary"/>, text: "Efficiency Tracking: Analyze average days in inventory to optimize turnover." },
    { icon: <AssignmentTurnedInIcon color="primary"/>, text: "Task Management: Keep track of incomplete tasks needing attention." },
    { icon: <BarChartIcon color="primary"/>, text: "Historical Trends: Visualize last 6 months of gross profit and reconditioning costs." },
    { icon: <TrendingUpIcon color="primary"/>, text: "Data-Driven Decisions: Leverage insights to improve profitability and operational efficiency." },
  ];

  return (
    <FeaturePage title="Performance Metrics">
      <Container maxWidth="xl" disableGutters sx={{ py: { xs: 4, md: 6 } }}>

        {/* Introductory Text */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          style={{ marginBottom: '4rem', textAlign: 'center', paddingLeft: '16px', paddingRight: '16px' }} // Add padding here since gutters are disabled
        >
          <Typography variant="h4" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Understand Your Dealership's Pulse.
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: '850px', mx: 'auto' }}>
            Gain instant visibility into your key performance indicators. DealCost aggregates critical data into intuitive dashboards and charts, empowering you to make informed decisions.
          </Typography>
        </motion.div>

        {/* Main Content Section */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={sectionVariants} sx={{ mb: { xs: 4, md: 6 } }}>
          {/* Image First */}
          <motion.div variants={imageVariants} sx={{ mb: 4 }}>
             <Paper elevation={6} sx={{ overflow: 'hidden', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.08)' }}> {/* No maxWidth, full width */}
               <Box component="img" src={performanceMetricsScreenshot} alt="DealCost Performance Metrics Dashboard" sx={{ width: '100%', display: 'block' }}/>
             </Paper>
             <Typography variant="caption" display="block" align="center" sx={{ mt: 1.5, color: 'text.secondary' }}>
                Comprehensive dashboard view of key dealership metrics.
             </Typography>
          </motion.div>

          {/* Text Below */}
          <Box sx={{ maxWidth: '800px', mx: 'auto', px: 2 }}> {/* Center text block, add padding */}
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, textAlign: 'center' }}>Actionable Insights at Your Fingertips</Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
               From high-level financial summaries to detailed inventory aging and historical trends, track the metrics that matter most to your bottom line.
             </Typography>
            <List>
              {metricsFeatures.map((feature, index) => (
                <motion.div key={index} variants={itemVariants} custom={index} initial="hidden" animate="visible" transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}>
                  <ListItem disablePadding sx={{ mb: 2, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: '40px', mt: '4px' }}>
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText primary={feature.text} primaryTypographyProps={{ variant: 'body1' }} />
                  </ListItem>
                </motion.div>
              ))}
            </List>
          </Box>
        </motion.div>

        {/* Optional: Add Divider and more sections if needed */}

      </Container>
    </FeaturePage>
  );
}

export default PerformanceMetricsPage;